import { useState } from "react";
import { api, type AuthUser } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  PASSWORD_MISMATCH,
  PASSWORD_REQUIREMENT,
  validatePassword,
} from "../lib/password";
import { PasswordChecklist } from "./PasswordChecklist";
import { PasswordInput } from "./PasswordInput";

const inputClass =
  "mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

export function ForcePasswordChange({ user }: { user: AuthUser }) {
  const { applyToken, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (validatePassword(newPassword)) {
      setError(PASSWORD_REQUIREMENT);
      return;
    }
    if (newPassword !== confirm) {
      setError(PASSWORD_MISMATCH);
      return;
    }
    setLoading(true);
    try {
      const res = await api.changePassword(currentPassword, newPassword);
      await applyToken(res.token);
    } catch (err) {
      setError((err as Error).message || "Ekki tókst að breyta lykilorði.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/40 px-4">
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md rounded-2xl border border-line bg-white p-6 shadow-[0_24px_60px_rgba(11,36,71,.2)]"
      >
        <h1 className="text-xl font-bold text-ink">Breyttu lykilorðinu þínu</h1>
        <p className="mt-2 text-sm text-muted-2">
          Þú ert skráð(ur) inn sem{" "}
          <span className="font-semibold">{user.email}</span>. Af öryggisástæðum
          þarftu að velja nýtt lykilorð áður en þú heldur áfram.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink">
              Núverandi lykilorð
            </label>
            <PasswordInput
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink">
              Nýtt lykilorð
            </label>
            <PasswordInput
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
              className={inputClass}
            />
            <PasswordChecklist password={newPassword} confirm={confirm} />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink">
              Staðfestu nýtt lykilorð
            </label>
            <PasswordInput
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              className={inputClass}
            />
          </div>

          {error && (
            <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-brand px-4 py-2 font-semibold text-white transition hover:bg-brand-dark disabled:opacity-50"
          >
            {loading ? "Vista…" : "Vista nýtt lykilorð"}
          </button>
          <button
            type="button"
            onClick={() => void logout()}
            className="w-full text-sm text-muted-2 underline underline-offset-2 hover:text-ink"
          >
            Skrá út og skipta um notanda
          </button>
        </form>
      </div>
    </div>
  );
}
